import React from "react";

// Top-level safety net. Without it, any render/runtime error unmounts the whole React
// tree and leaves a blank page (the site's background) with no way to recover — exactly
// the "flashes then goes blank" symptom. This catches the error, shows a recovery action,
// and surfaces the message so it can be reported. "Start fresh" clears any saved The Writing Studio
// session (a stale/old-shaped stored session is the most common cause) and reloads.
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep a trace in the console for anyone inspecting.
    // eslint-disable-next-line no-console
    console.error("The Writing Studio crashed:", error, info?.componentStack);
  }

  handleReset = () => {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("compass_") || k.startsWith("dws_"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      /* ignore */
    }
    // Return to the canonical public experience, cleanly.
    window.location.href = "/?experience";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error || "Unknown error");
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen paper-grain flex items-center justify-center px-6"
      >
        <div className="w-full max-w-md text-center">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-3">
            The Writing Studio
          </p>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-900 leading-snug">
            Something interrupted your session
          </h1>
          <p className="mt-3 text-[15px] text-stone-600 leading-relaxed">
            We hit an unexpected error. Starting fresh clears this session and takes you back to the
            beginning — your work isn't lost from The Writing Studio's records.
          </p>
          <button
            onClick={this.handleReset}
            data-testid="app-error-reset"
            className="mt-7 inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide uppercase text-[13px] hover:bg-[#6B2C20] transition-colors"
          >
            Start fresh
          </button>
          <pre
            data-testid="app-error-message"
            className="mt-8 text-left text-[11px] leading-relaxed text-stone-400 bg-white/60 border border-stone-200 rounded-sm p-3 overflow-x-auto whitespace-pre-wrap"
          >
            {msg}
          </pre>
        </div>
      </div>
    );
  }
}
